import { useState } from "react";
import {
  NotAuthenticatedError,
  api,
  type AgentEntry,
  type AgentRun,
} from "../api";
import { useApiData } from "../useApi";
import { PageState, formatTime } from "../components";

/**
 * Agent Control Room (docs/07-dashboard-governance-ops.md §2).
 *
 * Two decisions shape this page:
 *
 * **Unbuilt agents are shown, not hidden.** A monitoring page that lists only
 * what happens to be implemented cannot tell you what is missing, which is
 * the main thing worth knowing about a half-built system. Eleven agents are
 * specified; one exists.
 *
 * **Runs needing review come first.** They are the only reason to open this
 * page in a hurry, so they are not something you scroll to find.
 */
export function AgentControlRoom({
  onSignedOut,
  canManage,
}: {
  onSignedOut: () => void;
  canManage: boolean;
}) {
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  const catalog = useApiData(() => api.agents(), onSignedOut, [nonce]);
  const runs = useApiData(() => api.agentRuns(false, 30), onSignedOut, [nonce]);

  const needsReview = (runs.data?.runs ?? []).filter(
    (r) => r.requires_human_review,
  );
  const built = (catalog.data?.agents ?? []).filter((a) => a.implemented);
  const planned = (catalog.data?.agents ?? []).filter((a) => !a.implemented);

  return (
    <>
      <h1>Agent Control Room</h1>
      <p className="subtitle">
        {built.length} of {catalog.data?.agents.length ?? "…"} agents built.{" "}
        <button className="link" onClick={reload}>
          Refresh
        </button>
      </p>

      <PageState loading={catalog.loading} error={catalog.error}>
        {needsReview.length > 0 && (
          <div className="card">
            <h2>
              Needs your attention{" "}
              <span className="badge danger">{needsReview.length}</span>
            </h2>
            <p className="dim">
              An agent could not answer, or asked to be checked. Read the answer
              against the records it cites before believing it.
            </p>
            <RunTable runs={needsReview} />
          </div>
        )}

        <div className="card">
          <h2>Built</h2>
          {built.map((agent) => (
            <AgentCard
              key={agent.name}
              agent={agent}
              canManage={canManage}
              onChanged={reload}
              onSignedOut={onSignedOut}
            />
          ))}
        </div>

        <div className="card">
          <h2>Specified, not built yet</h2>
          <p className="dim">
            From docs/02-agents.md §2. Listed so the gap is visible rather than
            invisible.
          </p>
          <table>
            <thead>
              <tr>
                <th>Ref</th>
                <th>Agent</th>
                <th>What it would do</th>
                <th>Authority</th>
              </tr>
            </thead>
            <tbody>
              {planned.map((agent) => (
                <tr key={agent.name}>
                  <td>{agent.catalog_ref}</td>
                  <td>{agent.title}</td>
                  <td className="dim">{agent.purpose}</td>
                  <td>
                    <span className="badge">{agent.authority}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <h2>Recent runs</h2>
          <PageState
            loading={runs.loading}
            error={runs.error}
            empty={(runs.data?.runs ?? []).length === 0}
            emptyMessage="No agent has run yet."
          >
            <RunTable runs={runs.data?.runs ?? []} />
          </PageState>
        </div>
      </PageState>
    </>
  );
}

function AgentCard({
  agent,
  canManage,
  onChanged,
  onSignedOut,
}: {
  agent: AgentEntry;
  canManage: boolean;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await api.pauseAgent(agent.name, !agent.paused, reason);
      setReason("");
      onChanged();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <h3>
        {agent.title}{" "}
        <span className="badge">{agent.catalog_ref}</span>{" "}
        {agent.paused ? (
          <span className="badge warn">paused</span>
        ) : (
          <span className="badge ok">active</span>
        )}
      </h3>
      <p className="dim">{agent.purpose}</p>

      {agent.paused && (
        <div className="notice">
          Paused by {agent.paused_by}
          {agent.paused_reason ? ` — ${agent.paused_reason}` : ""}. It still
          answers, saying it is paused.
        </div>
      )}

      <div className="metrics">
        <Metric label="Runs" value={String(agent.runs)} />
        <Metric
          label="Failed"
          value={String(agent.failed)}
          tone={agent.failed > 0 ? "warn" : "ok"}
        />
        <Metric
          label="Needs review"
          value={String(agent.needs_review)}
          tone={agent.needs_review > 0 ? "danger" : "ok"}
        />
        <Metric
          label="Last latency"
          value={
            agent.last_latency_ms === null ? "—" : `${agent.last_latency_ms}ms`
          }
        />
        <Metric label="Model" value={agent.last_model ?? "—"} />
        <Metric label="Prompt" value={agent.prompt_version ?? "—"} />
        <Metric
          label="Last run"
          value={agent.last_run_at ? formatTime(agent.last_run_at) : "never"}
        />
        <Metric label="Authority" value={agent.authority} />
      </div>

      {canManage && (
        <>
          <label>
            Reason
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                agent.paused ? "why it is safe to resume" : "why you are pausing it"
              }
            />
          </label>
          <button
            className={agent.paused ? undefined : "danger"}
            disabled={busy || reason.trim() === ""}
            onClick={toggle}
          >
            {busy ? "Working…" : agent.paused ? "Resume" : "Pause"}
          </button>
          {error && <div className="notice error">{error}</div>}
        </>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "danger";
}) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">
        {tone ? <span className={`badge ${tone}`}>{value}</span> : value}
      </div>
    </div>
  );
}

function RunTable({ runs }: { runs: AgentRun[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>When</th>
          <th>Agent</th>
          <th>Model</th>
          <th>Latency</th>
          <th>Result</th>
          <th>Asked by</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <tr key={run.run_id}>
            <td title={run.trace_id}>{formatTime(run.started_at)}</td>
            <td>{run.agent}</td>
            <td className="dim">{run.model}</td>
            <td>{run.latency_ms}ms</td>
            <td>
              {run.ok ? (
                <span
                  className={`badge ${
                    run.requires_human_review ? "warn" : "ok"
                  }`}
                >
                  {run.confidence}
                  {run.requires_human_review ? " · review" : ""}
                </span>
              ) : (
                // The failures are the interesting rows, so the reason is
                // shown inline rather than hidden behind a click.
                <span className="badge danger" title={run.error ?? ""}>
                  failed
                </span>
              )}
            </td>
            <td className="dim">{run.requested_by}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
